import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getMoreApps, MoreAppItem } from "../services/api/apps";

interface MoreAppsContextType {
  apps: MoreAppItem[];
  loading: boolean;
  isOpen: boolean;
  openMoreApps: () => void;
  closeMoreApps: () => void;
  refreshApps: () => Promise<void>;
}

const MoreAppsContext = createContext<MoreAppsContextType | undefined>(undefined);

export const MoreAppsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [apps, setApps] = useState<MoreAppItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const checkAndShowDailyMoreAppsModal = () => {
    try {
      const todayStr = new Date().toISOString().split("T")[0];
      const lastDate = localStorage.getItem("more_apps_last_date");
      const countStr = localStorage.getItem("more_apps_launch_count");

      let launchCount = 1;
      if (lastDate === todayStr && countStr !== null) {
        launchCount = parseInt(countStr, 10) + 1;
      }

      localStorage.setItem("more_apps_last_date", todayStr);
      localStorage.setItem("more_apps_launch_count", String(launchCount));

      // Show modal on 1st, 3rd, and 5th visit/launch of the day
      if (launchCount === 1 || launchCount === 3 || launchCount === 5) {
        setTimeout(() => {
          setIsOpen(true);
        }, 800);
      }
    } catch (err) {
      console.log("Error in checkAndShowDailyMoreAppsModal:", err);
    }
  };

  const fetchApps = async () => {
    setLoading(true);
    try {
      const appData = await getMoreApps("english");
      setApps(appData || []);
    } catch (err) {
      console.error("Failed to fetch apps from API:", err);
      setApps([]);
    } finally {
      setLoading(false);
      checkAndShowDailyMoreAppsModal();
    }
  };

  useEffect(() => {
    fetchApps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openMoreApps = () => setIsOpen(true);
  const closeMoreApps = () => setIsOpen(false);
  const refreshApps = async () => {
    await fetchApps();
  };

  return (
    <MoreAppsContext.Provider
      value={{
        apps,
        loading,
        isOpen,
        openMoreApps,
        closeMoreApps,
        refreshApps,
      }}
    >
      {children}
    </MoreAppsContext.Provider>
  );
};

export const useMoreApps = (): MoreAppsContextType => {
  const context = useContext(MoreAppsContext);
  if (!context) {
    throw new Error("useMoreApps must be used within a MoreAppsProvider");
  }
  return context;
};
